import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router';
import { useOktaAuth } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';

/**
 * @okta/okta-react does not export the OktaError component SecureRoute fell
 * back to, so mirror what it rendered when the login redirect failed.
 */
const OktaError = ({ error }) => {
  if (error.name && error.message) {
    return (
      <p>
        {error.name}: {error.message}
      </p>
    );
  }

  return <p>Error: {error.toString()}</p>;
};

/**
 * Replacement for @okta/okta-react's <SecureRoute>, which only works with
 * react-router v5. Used as a pathless layout route: children render once the
 * agent is authenticated, otherwise we kick off the Okta redirect flow and
 * stash the requested URL so restoreOriginalUri can return them to it.
 */
const RequireAuth = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const pendingLogin = useRef(false);
  const [loginError, setLoginError] = useState(null);

  useEffect(() => {
    if (!authState) {
      return;
    }

    if (authState.isAuthenticated) {
      pendingLogin.current = false;
      return;
    }

    // Okta can emit several unauthenticated states before the browser leaves
    // the page, so only ever kick off one redirect.
    if (pendingLogin.current) {
      return;
    }
    pendingLogin.current = true;

    const handleLogin = async () => {
      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin,
      );
      oktaAuth.setOriginalUri(originalUri);
      await oktaAuth.signInWithRedirect();
    };

    // A misconfigured issuer/clientId or a network error fetching .well-known
    // rejects here. Surface it instead of leaving the agent on a blank page.
    handleLogin().catch((error) => {
      setLoginError(error);
    });
  }, [oktaAuth, authState]);

  if (loginError) {
    return <OktaError error={loginError} />;
  }

  if (!authState?.isAuthenticated) {
    return null;
  }

  return <Outlet />;
};

export default RequireAuth;
